import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import { ManageBoardDeletionStatus } from './ManageBoardDeletionStatus'
import { ManageInvitationStatus } from './ManageInvitationStatus'
import { useUpdatePersonTenantAssociationRole } from '../hooks/useUpdatePersonTenantAssociationRole'
import React, { useState } from 'react'

import Alert from '@material-ui/lab/Alert'
import { ApplyActivationCode } from './ApplyActivationCode'
import { BoardInvite } from './BoardInvite'
import { BoardStyle } from './BoardStyle'
import CachedIcon from '@material-ui/icons/Cached'
import { BoardLastAcknowledgment } from '../../shared/BoardLastAcknowledgment'
import { Channels } from '../../shared/Channels'
import { DigitalDevice } from './DigitalDevice'
import { FormattedDateTime } from '../../formatted-date-time'
import Helmet from 'react-helmet'
import { HubspotDealId } from './HubspotDealId'
import { InternalSwitch } from './InternalSwitch'
import { Link } from '../../link'
import { Messages } from './Messages'
import { TruncatedId } from '../../truncated-id'
import { useAddPlusSubscription } from '../hooks/useAddPlusSubscription'
import { useBoardDetailQuery } from '../hooks/useBoardDetailQuery'
import { useCreateCheckoutToken } from '../hooks/useCreateCheckoutToken'
import { useGrantPlusSubscription } from '../hooks/useGrantPlusSubscription'
import { usePlusSubscriptionV2Query } from '../hooks/usePlusSubscriptionV2Query'
import { useDeleteBoard } from '../hooks/useDeleteBoard'
import { useExpirePlusSubscription } from '../hooks/useExpirePlusSubscription'
import { usePlusSyncStatus } from '../hooks/usePlusSyncStatus'
import { useResetPlusSubscription } from '../hooks/useResetPlusSubscription'
import { useHistory } from 'react-router'
import { useListDisplayGroups } from '../../note-array/hooks/useListDisplayGroups'
import { useUncacheBoard } from '../hooks/useUnCacheBoard'
import { useUnpin } from '../../../api/useUnpin'

interface IBoardDetailProps {
  boardId: string
}

const useStyles = makeStyles({
  title: {
    paddingBottom: 14
  },
  devicesTitle: {
    paddingTop: 32,
    paddingBottom: 14
  }
})

export const BoardDetail = (props: IBoardDetailProps) => {
  const classes = useStyles()
  const history = useHistory()

  const [confirmAddPlus, setConfirmAddPlus] = React.useState(false)
  const [mutate] = useUpdatePersonTenantAssociationRole()
  const [expirePlusSubscription] = useExpirePlusSubscription()
  const [addPlusSubscription] = useAddPlusSubscription()
  const [resetPlusSubscription] = useResetPlusSubscription()
  const [grantPlusSubscription] = useGrantPlusSubscription()
  const [createCheckoutToken] = useCreateCheckoutToken()
  const [confirmExpire, setConfirmExpire] = React.useState(false)
  const [confirmResetStripe, setConfirmResetStripe] = React.useState(false)
  const [confirmGrantStripe, setConfirmGrantStripe] = React.useState(false)
  const [stripeResetting, setStripeResetting] = React.useState(false)
  const [showMessages, setShowMessages] = React.useState(false)
  const [expiring, setExpiring] = React.useState(false)
  const [roleChanges, setRoleChanges] = React.useState<{ [key: string]: string }>({})
  const { data, loading, error, refetch } = useBoardDetailQuery(props.boardId)
  const { data: plusSubData, loading: plusSubLoading, refetch: refetchPlusSub } = usePlusSubscriptionV2Query(props.boardId)
  const { syncStatus, loading: syncLoading, error: syncError, sync, syncing, refetch: refetchSync } = usePlusSyncStatus(props.boardId)
  const { addToast } = useToasts()
  const [isUnpinning, setIsUnpinning] = React.useState(false)
  const [unpinMutation, { loading: unpinning }] = useUnpin()
  const [unCache] = useUncacheBoard()
  const [deleteBoard, { loading: deleting }] = useDeleteBoard()
  const [deletingBoard, setDeletingBoard] = useState<string | null>(null)
  const { data: displayGroupsData } = useListDisplayGroups({
    input: {
      all: true
    }
  })

  // Find the Note Array this board belongs to, if any
  const noteArrayInfo = React.useMemo(() => {
    const groups = displayGroupsData?.listDisplayGroups?.displayGroups || []
    for (const group of groups) {
      const boardIds = group.boards.flat().map(b => b?.id)
      if (boardIds.includes(props.boardId)) {
        return { id: group.id, title: group.title }
      }
    }
    return null
  }, [displayGroupsData, props.boardId])

  if (loading) {
    return <CircularProgress />
  }

  if (error) {
    return <Alert severity='error'>Board not found.</Alert>
  }

  // Redirect to Note Array detail page if boardStyle includes 'group'
  if (data?.board?.boardStyle?.includes('group')) {
    history.replace(`/note-arrays/${props.boardId}`)
    return <CircularProgress />
  }

  const boardV2 = data?.getBoardV2?.__typename === 'BoardV2' ? data.getBoardV2 : null
  const v2OutgoingWebhooks = boardV2?.outgoingWebhooks ?? []

  // const v2OutgoingWebhooks = data?
  const personRows =
    data?.board.tenant.__typename === 'PersonTenant'
      ? data.board.tenant.members
          .filter(m => m.isCurrentMember)
          .map(member => (
            <TableRow key={member.id}>
              <TableCell>
                <Button variant='outlined' onClick={() => history.push(`/person/${member.person.id}`)}>
                  <TruncatedId value={member.person.id} />
                </Button>
              </TableCell>
              <TableCell>
                <Select
                  value={roleChanges[member.id] ?? member.role}
                  onChange={e => {
                    setRoleChanges({
                      ...roleChanges,
                      [member.id]: e.target.value as string
                    })
                    mutate({
                      variables: {
                        personTenantAssociationId: member.id,
                        newRole: e.target.value as string
                      }
                    })
                  }}>
                  <MenuItem value={'Owner'}>Owner</MenuItem>
                  <MenuItem value={'Admin'}>Admin</MenuItem>
                  <MenuItem value={'User'}>User</MenuItem>
                </Select>
              </TableCell>
              <TableCell>
                <FormattedDateTime value={member.created} />
              </TableCell>
              <TableCell>
                {member.person.firstName} {member.person.lastName}
              </TableCell>
              <TableCell>{member.person?.account?.emailAddress}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          ))
      : null

  const revokedPersonRows =
    data?.board.tenant.__typename === 'PersonTenant'
      ? data.board.tenant.members
          .filter(m => !m.isCurrentMember)
          .map(member => (
            <TableRow style={{ background: 'gray' }} key={member.id}>
              <TableCell>
                <Button variant='outlined' onClick={() => history.push(`/person/${member.person.id}`)}>
                  <TruncatedId value={member.person.id} />
                </Button>
              </TableCell>
              <TableCell>{member.role}</TableCell>
              <TableCell>
                <FormattedDateTime value={member.created} />
              </TableCell>
              <TableCell>
                {member.person.firstName} {member.person.lastName}
              </TableCell>
              <TableCell>{member.person.account.emailAddress}</TableCell>
              <TableCell>
                <ManageInvitationStatus
                  boardTitle={data.board.title}
                  personName={`${member.person.firstName} ${member.person.lastName}`}
                  personEmailAddress={member.person?.account?.emailAddress || ''}
                  personTenantAssociationId={member.id}
                  status={member.invitationStatus}
                  personId={member.person.id}
                  boardId={data.board.id}
                  onClose={() => refetch()}
                />
              </TableCell>
            </TableRow>
          ))
      : null

  const doExpire = async () => {
    setExpiring(true)
    await expirePlusSubscription({ variables: { boardId: data?.board.id ?? '' } })
    await refetch()
    setExpiring(false)
    setConfirmExpire(false)
  }

  const doAddPlus = async () => {
    setExpiring(true)
    await addPlusSubscription({ variables: { boardId: data?.board.id ?? '' } })
    await refetch()
    setExpiring(false)
    setConfirmAddPlus(false)
  }

  const plusProductApplication = data?.board.productApplications
    ?.filter(pa => pa.pricing?.product?.isPlus)
    ?.sort((a, b) => (a.ends > b.ends ? -1 : a.ends < b.ends ? 1 : 0))[0]

  const isVirtual = data?.board && !data?.board?.devices?.length

  return (
    <Box>
      {showMessages ? <Messages onClose={() => setShowMessages(false)} boardId={data?.board?.id || ''} /> : null}
      <Helmet>
        <meta charSet='utf-8' />
        <title>Board - {data?.board.title} - Detail Page - Vestaboard Superadmin</title>
      </Helmet>
      <Dialog maxWidth='lg' open={!!deletingBoard} onClose={() => setDeletingBoard(null)}>
        <DialogTitle>Delete the virtual board "{data?.board.title}"?</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this virtual board?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingBoard(null)}>No, Cancel</Button>
          <Button
            color='primary'
            variant='outlined'
            onClick={async () => {
              await deleteBoard({
                variables: {
                  input: {
                    boardId: deletingBoard as string
                  }
                }
              })
              await refetch()
              setDeletingBoard(null)
            }}>
            {deleting ? <CircularProgress /> : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={confirmExpire} onClose={() => setConfirmExpire(false)}>
        <DialogTitle>Remove Plus subscription from board</DialogTitle>
        <DialogContent dividers>
          Are you sure you would like to remove the Plus subscription for this board?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAddPlus(false)}>{expiring ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button color='primary' variant='outlined' onClick={() => doExpire()}>
            {expiring ? <CircularProgress /> : 'Yes, Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={confirmAddPlus} onClose={() => setConfirmAddPlus(false)}>
        <DialogTitle>Add Plus subscription to board</DialogTitle>
        <DialogContent dividers>Are you sure you would like to add a Plus subscription to this board?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExpire(false)}>{expiring ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button color='primary' variant='outlined' onClick={() => doAddPlus()}>
            {expiring ? <CircularProgress /> : 'Yes, Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={confirmResetStripe} onClose={() => setConfirmResetStripe(false)}>
        <DialogTitle>Reset Stripe Plus subscription</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to cancel the Stripe subscription and remove Plus from this board?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetStripe(false)}>{stripeResetting ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button
            color='primary'
            variant='outlined'
            onClick={async () => {
              setStripeResetting(true)
              await resetPlusSubscription({ variables: { boardId: data?.board.id ?? '' } })
              await refetchPlusSub()
              setStripeResetting(false)
              setConfirmResetStripe(false)
            }}>
            {stripeResetting ? <CircularProgress /> : 'Yes, Reset'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={confirmGrantStripe} onClose={() => setConfirmGrantStripe(false)}>
        <DialogTitle>Grant Plus subscription</DialogTitle>
        <DialogContent dividers>
          Are you sure you would like to grant a Plus subscription to this board?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGrantStripe(false)}>{stripeResetting ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button
            color='primary'
            variant='outlined'
            onClick={async () => {
              setStripeResetting(true)
              await grantPlusSubscription({ variables: { boardId: data?.board.id ?? '' } })
              await refetchPlusSub()
              setStripeResetting(false)
              setConfirmGrantStripe(false)
            }}>
            {stripeResetting ? <CircularProgress /> : 'Yes, Grant'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth='lg' open={isUnpinning} onClose={() => setIsUnpinning(false)}>
        <DialogTitle>Unpin Message</DialogTitle>
        <DialogContent dividers>Are you sure you want to unpin the message from this board?</DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUnpinning(false)}>{unpinning ? <CircularProgress /> : 'No, Cancel'}</Button>
          <Button
            color='primary'
            variant='outlined'
            onClick={async () => {
              await unpinMutation({
                variables: {
                  input: {
                    boardId: data?.board.id || ''
                  }
                }
              })
              await new Promise(resolve => {
                setTimeout(async () => {
                  await refetch()
                  resolve(true)
                }, 2500)
              })
              setIsUnpinning(false)
            }}>
            {unpinning ? <CircularProgress /> : 'Yes, Unpin'}
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant='button' display='block' gutterBottom>
        <Link to='/boards'>Board</Link>
      </Typography>
      <Typography variant='h4' className={classes.title}>
        {data?.board.title || props.boardId}
      </Typography>
      <Box>
        <Grid container>
          <Grid item md={6} sm={12}>
            <Card>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Board Id</strong>
                    </TableCell>
                    <TableCell>{data?.board.id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Board Friendly Identifier</strong>
                    </TableCell>
                    <TableCell>{data?.board.friendlyIdentifier}</TableCell>
                  </TableRow>
                  {noteArrayInfo && (
                    <TableRow>
                      <TableCell>
                        <strong>Note Array</strong>
                      </TableCell>
                      <TableCell>
                        <Link to={`/note-arrays/${noteArrayInfo.id}`}>{noteArrayInfo.title || 'Untitled'}</Link>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      <strong>Board Title</strong>
                    </TableCell>
                    <TableCell>{data?.board.title || 'Untitled'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Display Type</strong>
                    </TableCell>
                    <TableCell>
                      <BoardStyle
                        disabled={false}
                        id={data?.board.boardStyle as string}
                        boardId={data?.board.id as string}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Last Message</strong>
                    </TableCell>
                    <TableCell>
                      {boardV2?.listMessageAppearances?.messageAppearances?.length ? (
                        <FormattedDateTime value={boardV2.listMessageAppearances.messageAppearances[0].sentAt} />
                      ) : (
                        '(none)'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Last Device Message Acknowledgment</strong>
                    </TableCell>
                    <TableCell>
                      <BoardLastAcknowledgment boardId={data?.board.id || ''} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Timezone</strong>
                    </TableCell>
                    <TableCell>
                      {data?.board.timezone?.timezoneCity || data?.board?.devices?.[0]?.timezone || '(none)'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Internal Board</strong>
                    </TableCell>
                    <TableCell>
                      <InternalSwitch boardId={data?.board?.id || ''} internalBoard={!!data?.board?.internalBoard} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Hubspot Deal ID</strong>
                    </TableCell>
                    <TableCell>
                      <HubspotDealId boardId={data?.board?.id || ''} hubspotDealId={data?.board?.hubspotDealId || ''} />
                    </TableCell>
                  </TableRow>
                  <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={2}>
                      <Typography variant='subtitle2'>Shopify / Legacy Plus</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Is Plus</strong>
                    </TableCell>
                    <TableCell>
                      <Switch
                        onChange={e => {
                          if (e.target.checked === false) {
                            setConfirmExpire(true)
                          } else {
                            setConfirmAddPlus(true)
                          }
                        }}
                        checked={!!plusProductApplication}
                      />
                    </TableCell>
                  </TableRow>
                  {!!plusProductApplication ? (
                    <>
                      <TableRow>
                        <TableCell>
                          <strong>Shopify Subscription ID</strong>
                        </TableCell>
                        <TableCell>
                          <TruncatedId value={plusProductApplication.id} path={`/plus/${plusProductApplication.id}`} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Shopify Activation Date</strong>
                        </TableCell>
                        <TableCell>
                          <FormattedDateTime value={plusProductApplication.started} hideTime />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Shopify Ending Date</strong>
                        </TableCell>
                        <TableCell>
                          <FormattedDateTime value={plusProductApplication.ends} hideTime />
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell>
                        <strong>Plus Redemption Code</strong>
                      </TableCell>
                      <TableCell>
                        <ApplyActivationCode boardId={data?.board?.id || ''} />
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={2}>
                      <Typography variant='subtitle2'>Stripe Plus</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Is Plus (Stripe)</strong>
                    </TableCell>
                    <TableCell>
                      <Switch
                        onChange={e => {
                          if (e.target.checked === false) {
                            setConfirmResetStripe(true)
                          } else {
                            setConfirmGrantStripe(true)
                          }
                        }}
                        checked={plusSubData?.plusSubscriptionV2?.__typename === 'PlusSubscriptionV2'}
                      />
                    </TableCell>
                  </TableRow>
                  {!plusSubLoading && plusSubData?.plusSubscriptionV2?.__typename !== 'PlusSubscriptionV2' && (
                    <TableRow>
                      <TableCell>
                        <strong>Purchase Link</strong>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant='outlined'
                          onClick={async () => {
                            try {
                              const result = await createCheckoutToken({
                                variables: { boardId: data?.board.id ?? '', boardTitle: data?.board.title }
                              })
                              const tokenData = result.data?.createCheckoutTokenV2
                              if (tokenData?.__typename === 'CheckoutTokenV2') {
                                await navigator.clipboard.writeText(tokenData.url)
                                addToast('Purchase link copied to clipboard', { appearance: 'success' })
                              } else if (tokenData?.__typename === 'CheckoutTokenErrorV2') {
                                addToast(tokenData.error ?? 'Failed to create purchase link', { appearance: 'error' })
                              }
                            } catch {
                              addToast('Failed to create purchase link', { appearance: 'error' })
                            }
                          }}>
                          Copy Purchase Link
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {plusSubData?.plusSubscriptionV2?.__typename === 'PlusSubscriptionV2' && (
                    <>
                      {plusSubData.plusSubscriptionV2.paymentProvider === 'admin' ? (
                        <TableRow>
                          <TableCell>
                            <strong>Source</strong>
                          </TableCell>
                          <TableCell>
                            <Alert severity='info' style={{ display: 'inline-flex' }}>
                              Admin-granted — no Stripe subscription
                            </Alert>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Subscription ID</strong>
                          </TableCell>
                          <TableCell>
                            <a
                              href={`https://dashboard.stripe.com/subscriptions/${plusSubData.plusSubscriptionV2.subscriptionId}`}
                              target='_blank'
                              rel='noopener noreferrer'>
                              {plusSubData.plusSubscriptionV2.subscriptionId}
                            </a>
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.activationDate && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Activation Date</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime
                              value={plusSubData.plusSubscriptionV2.billingInformation.activationDate * 1000}
                              hideTime
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.cardBrand && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Payment Method</strong>
                          </TableCell>
                          <TableCell>
                            {plusSubData.plusSubscriptionV2.billingInformation.cardBrand} ****
                            {plusSubData.plusSubscriptionV2.billingInformation.cardLast4}
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.purchaserEmail && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Purchaser Email</strong>
                          </TableCell>
                          <TableCell>{plusSubData.plusSubscriptionV2.billingInformation.purchaserEmail}</TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.interval && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Billing Interval</strong>
                          </TableCell>
                          <TableCell>
                            Every {plusSubData.plusSubscriptionV2.billingInformation.intervalCount}{' '}
                            {plusSubData.plusSubscriptionV2.billingInformation.interval}(s)
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.nextBillingDate && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Next Billing Date</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime
                              value={plusSubData.plusSubscriptionV2.billingInformation.nextBillingDate * 1000}
                              hideTime
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.cancellationDate && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Cancellation Date</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime
                              value={plusSubData.plusSubscriptionV2.billingInformation.cancellationDate * 1000}
                              hideTime
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingInformation?.activationCode && (
                        <TableRow>
                          <TableCell>
                            <strong>Stripe Activation Code</strong>
                          </TableCell>
                          <TableCell>{plusSubData.plusSubscriptionV2.billingInformation.activationCode}</TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.currentPeriodStart != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Current Period Start</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.currentPeriodStart} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.currentPeriodEnd != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Current Period End</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.currentPeriodEnd} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.subscriptionCreated != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Subscription Created</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.subscriptionCreated} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.startDate != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Start Date</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.startDate} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.trialStart != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Trial Start</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.trialStart} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.trialEnd != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Trial End</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.trialEnd} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.canceledAt != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Canceled At</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.canceledAt} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.cancelAt != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Cancel At</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.cancelAt} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.endedAt != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Ended At</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.endedAt} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                      {plusSubData.plusSubscriptionV2.billingCycleAnchor != null && (
                        <TableRow>
                          <TableCell>
                            <strong>Billing Cycle Anchor</strong>
                          </TableCell>
                          <TableCell>
                            <FormattedDateTime value={plusSubData.plusSubscriptionV2.billingCycleAnchor} hideTime />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                  <TableRow>
                    <TableCell>
                      <strong>Stripe Sync Status</strong>
                    </TableCell>
                    <TableCell>
                      {syncLoading ? (
                        <CircularProgress size={16} />
                      ) : syncStatus ? (
                        <FlexHorizontal>
                          {syncStatus.inSync ? (
                            <Alert severity='success' style={{ display: 'inline-flex', padding: '0 8px' }}>
                              In sync
                            </Alert>
                          ) : (
                            <Alert severity='error' style={{ display: 'inline-flex', padding: '0 8px' }}>
                              Out of sync: {syncStatus.mismatches.join(', ')}
                            </Alert>
                          )}
                          <Spacer size='small' />
                          <Button
                            variant='outlined'
                            size='small'
                            disabled={syncing}
                            onClick={async () => {
                              try {
                                const result = await sync()
                                if (result?.inSync) {
                                  addToast('Plus subscription synced', { appearance: 'success' })
                                  refetchPlusSub()
                                } else {
                                  addToast(`Sync completed but still out of sync: ${result?.mismatches.join(', ')}`, {
                                    appearance: 'error'
                                  })
                                }
                                refetchSync()
                              } catch {
                                addToast('Failed to sync Plus subscription', { appearance: 'error' })
                              }
                            }}>
                            {syncing ? <CircularProgress size={16} /> : 'Sync'}
                          </Button>
                        </FlexHorizontal>
                      ) : (
                        <Alert severity='error' style={{ display: 'inline-flex', padding: '0 8px' }}>
                          {syncError?.message ?? 'Failed to load sync status'}
                        </Alert>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Quiet hours</strong>
                    </TableCell>
                    <TableCell>
                      {data?.board.quietHoursBegin ?? 'None'} {data?.board.quietHoursEnd}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Pinned message until</strong>
                    </TableCell>
                    <TableCell>
                      {boardV2?.pinnedMessage ? (
                        <Box>
                          <FormattedDateTime value={boardV2.pinnedMessage.pinUntil}></FormattedDateTime>
                          <Spacer size='small' />
                          <Button
                            variant='outlined'
                            onClick={() => {
                              setIsUnpinning(true)
                            }}>
                            Unpin
                          </Button>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <strong>Messages</strong>
                    </TableCell>
                    <TableCell>
                      <Button variant='outlined' onClick={() => setShowMessages(showMessages => !showMessages)}>
                        Show Messages
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <strong>Web app link</strong>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outlined'
                        onClick={() => {
                          window.open(`https://web.vestaboard.com/board/${data?.board.id}/settings/`)
                        }}>
                        Use board in web app
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Live</strong>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outlined'
                        onClick={() => {
                          history.push(`/boards/${data?.board.id}/live`)
                        }}>
                        Live View
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isVirtual && !data?.board.deleted ? (
                    <TableRow>
                      <TableCell>Delete</TableCell>
                      <TableCell>
                        <Button
                          variant='outlined'
                          color='secondary'
                          onClick={async () => {
                            setDeletingBoard(data?.board.id || null)
                          }}>
                          Delete Virtual Board
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {data?.board.deleted ? (
                    <TableRow style={{ background: 'red' }}>
                      <TableCell>
                        <strong>Deleted</strong>
                      </TableCell>
                      <TableCell>
                        <FormattedDateTime value={data.board.deleted} />
                        <ManageBoardDeletionStatus boardId={data.board.id} onClose={() => refetch()} />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Card>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item md={12}>
            <Typography variant='h5' className={classes.devicesTitle}>
              Users
            </Typography>
            <BoardInvite boardId={props.boardId} />
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Person ID</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Associated</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email Address</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {personRows}
                  {revokedPersonRows}
                </TableBody>
              </Table>
              {!data?.board?.devices?.length && (
                <Alert severity='info'>There are no devices associated with this board.</Alert>
              )}
            </Card>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={12}>
            <Channels subscriptions={data?.board?.subscriptionSet?.subscriptions} className={classes.devicesTitle} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={12}>
            <Typography variant='h5' className={classes.devicesTitle}>
              Flagship Devices
            </Typography>
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Device ID</TableCell>
                    <TableCell>Device Type</TableCell>
                    <TableCell>Device Model</TableCell>
                    <TableCell>Network SSID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.board?.devices || []).map(device => (
                    <TableRow key={device.id}>
                      <TableCell>
                        <Link to={`/devices/${device.id}`}>
                          <TruncatedId full value={device.id} />
                        </Link>
                      </TableCell>
                      <TableCell>{device.type}</TableCell>
                      <TableCell>{device?.model?.title || '(unknown)'}</TableCell>
                      <TableCell>{device?.configurationNetwork?.ssid || '(unknown)'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!data?.board?.devices?.length && (
                <Alert severity='info'>There are no devices associated with this board.</Alert>
              )}
            </Card>
          </Grid>
        </Grid>
        <DigitalDevice id={data?.board.id} />
        <Grid container>
          <Grid item md={12}>
            <Typography variant='h5' className={classes.devicesTitle}>
              V2 Outgoing Webhooks
            </Typography>
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Installable ID</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Webhook Token</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {v2OutgoingWebhooks.map((webhook: any) => (
                    <TableRow key={webhook.id}>
                      <TableCell>
                        <Link to={`/installables/${webhook.installableId}`}>
                          <TruncatedId full value={webhook.installableId} />
                        </Link>
                      </TableCell>
                      <TableCell>{webhook.url}</TableCell>
                      <TableCell>{webhook.token}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {v2OutgoingWebhooks?.length === 0 && (
                <Alert severity='info'>There are no v2 outgoing webhooks for this board.</Alert>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Box />
      <Spacer size='extraLarge' />
      <FlexHorizontal>
        <Button
          onClick={async () => {
            await unCache({
              variables: {
                input: {
                  id: props.boardId
                }
              }
            })
            await refetch()

            addToast('Redis cache cleared', {
              appearance: 'success'
            })
          }}>
          <CachedIcon />
          <Typography>&nbsp; Clear Cache</Typography>
        </Button>
      </FlexHorizontal>
    </Box>
  )
}
